import React, { ClipboardEvent, ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/_new/Button';
import { ProgressBar } from 'components/_new/ProgressBar';
import { ImageGalleryViewer } from 'components/_new/ImageGalleryViewer';
import { FieldAlert, FieldAlertRef } from 'components/_new/FieldAlert';
import { classNames } from 'primereact/utils';
import { clearInput } from 'utils/clearInput';
import { CHAT_ALLOWED_IMAGE_MIME_TYPES, CHAT_MAX_IMAGE_SIZE_MB } from './chatImageUpload.constants';
import { DraftChatImage } from './useChatImageUploads';

type ChatMessageComposerProps = {
	message: string;
	onMessageChange: (value: string) => void;
	onMessageBlur: (value: string) => void;
	onSubmit: () => void;
	onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	onPasteImages: (files: File[]) => void;
	onSelectImages: (files: File[]) => void;
	images: DraftChatImage[];
	onCancelImage: (localId: string) => void;
	onRetryImage: (localId: string) => void;
	onRemoveImage: (localId: string) => void;
	validationMessage?: string | null;
	sendDisabled?: boolean;
	sendLoading?: boolean;
	placeholder: string;
	sendLabel: string;
};

export const ChatMessageComposer = ({
	message,
	onMessageChange,
	onMessageBlur,
	onSubmit,
	onKeyDown,
	onPasteImages,
	onSelectImages,
	images,
	onCancelImage,
	onRetryImage,
	onRemoveImage,
	validationMessage = null,
	sendDisabled = false,
	sendLoading = false,
	placeholder,
	sendLabel,
}: ChatMessageComposerProps) => {
	const { t } = useTranslation();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const validationAlertRef = useRef<FieldAlertRef>(null);
	const [viewerVisible, setViewerVisible] = useState(false);
	const [activeIndex, setActiveIndex] = useState(0);
	const imageUploadTooltip = t('chat.addImageTooltip', {
		maxSizeMb: CHAT_MAX_IMAGE_SIZE_MB,
		types: CHAT_ALLOWED_IMAGE_MIME_TYPES.join(', '),
	});

	useEffect(() => {
		if (validationMessage) {
			validationAlertRef.current?.show();
		}
	}, [validationMessage]);

	const galleryImages = useMemo(
		() =>
			images.map((image) => ({
				id: image.uploadedImage?.id || image.localId,
				thumbUrl: image.previewUrl,
				imageUrl: image.uploadedImage?.imageUrl || image.previewUrl,
			})),
		[images]
	);
	const previewImages = useMemo(() => images.slice(0, 5), [images]);

	const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
		const files = Array.from(event.clipboardData.items)
			.filter((item) => item.kind === 'file')
			.map((item) => item.getAsFile())
			.filter(Boolean) as File[];
		if (files.length > 0) {
			event.preventDefault();
			onPasteImages(files);
		}
	};

	const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		if (files.length > 0) {
			onSelectImages(files);
		}
		if (fileInputRef.current) {
			clearInput(fileInputRef.current);
		}
	};

	return (
		<>
			{images.length > 0 ? (
				<div className="chat-draft-images">
					{previewImages.map((image, index) => {
						const isMoreTile = index === 4 && images.length > 5;
						const remainingCount = images.length - 4;

						return (
							<div key={image.localId} className="chat-draft-image-card">
								<button
									type="button"
									className={classNames('chat-draft-image-card__preview', {
										'chat-draft-image-card__preview-more': isMoreTile,
									})}
									onClick={() => {
										const nextIndex = galleryImages.findIndex(
											(galleryImage) =>
												galleryImage.id === (image.uploadedImage?.id || image.localId)
										);
										setActiveIndex(nextIndex >= 0 ? nextIndex : index);
										setViewerVisible(true);
									}}
								>
									<img src={image.previewUrl} alt="" />
									{isMoreTile ? (
										<span className="chat-draft-image-card__more-label">+{remainingCount}</span>
									) : null}
									<div className="chat-draft-image-card__status">
										{image.status === 'uploading' && !isMoreTile ? (
											<>
												<div className="chat-draft-image-card__status-label">
													{t('chat.imageUploadingProgress', { progress: image.progress })}
												</div>
												<ProgressBar current={image.progress} max={100} />
											</>
										) : null}
										{image.status === 'error' && !isMoreTile ? (
											<div className="chat-draft-image-card__status-label is-error">
												{image.errorMessage || t('chat.imageUploadFailed')}
											</div>
										) : null}
									</div>
								</button>
								{!isMoreTile ? (
									<div className="chat-draft-image-card__actions">
										{image.status === 'uploading' ? (
											<Button
												label={t('chat.cancelUpload')}
												icon="times"
												iconOnly
												variant="danger"
												variantSize="xs"
												circled
												onClick={() => onCancelImage(image.localId)}
											/>
										) : null}
										{image.status === 'error' ? (
											<Button
												label={t('chat.retryUpload')}
												icon="rotate-right"
												iconOnly
												variant="primary"
												variantSize="xs"
												circled
												onClick={() => onRetryImage(image.localId)}
											/>
										) : null}
										{image.status === 'uploaded' ? (
											<Button
												label={t('chat.removeImage')}
												icon="trash"
												iconOnly
												variant="danger"
												variantSize="xs"
												circled
												onClick={() => onRemoveImage(image.localId)}
											/>
										) : null}
									</div>
								) : null}
							</div>
						);
					})}
				</div>
			) : null}
			<form
				onSubmit={(event) => {
					event.preventDefault();
					onSubmit();
				}}
				className="flex-0 flex flex-column gap-2 pt-1"
			>
				<div className="relative">
					<FieldAlert
						ref={validationAlertRef}
						message={validationMessage || ''}
						variant="danger"
						displayDurationMs={3000}
						absolute
					/>
					<div className={classNames('input-base chat-message-composer')}>
						<textarea
							value={message}
							onChange={(event) => onMessageChange(event.target.value)}
							onBlur={(event) => onMessageBlur(event.target.value)}
							onKeyDown={onKeyDown}
							onPaste={handlePaste}
							rows={2}
							maxLength={1000}
							placeholder={placeholder}
							autoComplete="off"
							className="chat-message-composer__textarea"
						/>
						<input
							ref={fileInputRef}
							type="file"
							accept={CHAT_ALLOWED_IMAGE_MIME_TYPES.join(',')}
							multiple
							className="hidden-accessible"
							onChange={handleInputChange}
						/>
						<Button
							label={t('chat.addImage')}
							icon="image"
							iconOnly
							variant="grey-text"
							variantSize="sm"
							circled
							tooltip={imageUploadTooltip}
							tooltipPosition="top"
							onClick={() => fileInputRef.current?.click()}
						/>
					</div>
				</div>
				<div className="flex flex-col justify-content-end pt-1">
					<Button
						submit
						label={sendLabel}
						iconStyle="solid"
						loading={sendLoading}
						variant="primary"
						disabled={sendDisabled}
					/>
				</div>
			</form>
			<ImageGalleryViewer
				images={galleryImages}
				activeIndex={activeIndex}
				visible={viewerVisible}
				onClose={() => setViewerVisible(false)}
				onChangeIndex={setActiveIndex}
			/>
		</>
	);
};
